import { Music24PlanTier } from 'fym-common/src/license/music24Plans';
import { MUSIC24_MONTHLY_PRICE_USD, MUSIC24_MONTHS_PER_YEARLY_PRICE } from 'fym-shared/src/music24/music24Plans';

export interface Music24PlanPrice {
  /** USD cents; EUR shows the same number. From the plan table in fym-shared. */
  amount: number;
  /**
   * Stripe price ids of the 2026-08-24 prices (staging and production). Old marketing links carry
   * one as `checkoutPriceId`; `parseCheckoutIntent` maps it to the plan. A checkout never sends a
   * price id: it sends the tier and the interval, and the server finds the current price.
   */
  legacyPriceIds: readonly string[];
}

/** The plan ids `MUSIC24_PLANS` sells. Free and trial are not sold, so they never appear here. */
export type Music24PlanId = 'artist' | 'pro' | 'plug' | 'manager' | 'label';

/**
 * The Plug plan: the Pro plan for one artist plus Outreach (250 pitches a month, autopilot, two
 * own mailboxes). It is not sold any more (plans rename 2026-10-23): a workspace on Plug keeps it,
 * and the upgrade prompts never offer it (`plansForFeature` in `music24PlanCapabilities.ts`).
 */
export const MUSIC24_OUTREACH_PLAN_ID: Music24PlanId = 'plug';

export interface Music24Plan {
  id: Music24PlanId;
  /** The `Music24Capabilities` tier this plan grants — reads `music24PlanCapabilities[tier]`. */
  tier: Exclude<Music24PlanTier, 'free' | 'trial'>;
  nameMessageId: string;
  nameDefault: string;
  descriptionMessageId: string;
  descriptionDefault: string;
  mostPopular: boolean;
  prices: {
    monthly: Music24PlanPrice;
    yearly: Music24PlanPrice;
  };
}

export type BillingInterval = 'monthly' | 'yearly';

function pricesOf(
  tier: Music24PlanId,
  legacy: { monthly: readonly string[]; yearly: readonly string[] }
): Music24Plan['prices'] {
  const monthlyCents = Math.round(MUSIC24_MONTHLY_PRICE_USD[tier] * 100);
  return {
    monthly: { amount: monthlyCents, legacyPriceIds: legacy.monthly },
    yearly: { amount: monthlyCents * MUSIC24_MONTHS_PER_YEARLY_PRICE, legacyPriceIds: legacy.yearly },
  };
}

/**
 * The plans on sale, in rank order: the order of the plan cards. Without Plug the list is cheapest
 * first (`MUSIC24_GENERAL_PLANS`). Pro is the plan we push (`mostPopular`). Every price is for one
 * artist: extra artists are sold only in Settings -> Subscription (owner, 2026-10-23).
 */
export const MUSIC24_PLANS: Music24Plan[] = [
  {
    id: 'artist',
    tier: 'artist',
    nameMessageId: 'app.sml.pricing.plan.artist.name',
    nameDefault: 'Plus',
    descriptionMessageId: 'app.sml.pricing.plan.artist.description',
    descriptionDefault: 'For an artist who releases often.',
    mostPopular: false,
    prices: pricesOf('artist', {
      monthly: ['price_1U81OWH5lKCcz1F2tOQxBYdr', 'price_1U81N4HDHBS69TCVmfKJHrSw'],
      yearly: ['price_1U81OYH5lKCcz1F26eD8dVnl', 'price_1U81NBHDHBS69TCVMx6bnnkg'],
    }),
  },
  {
    id: 'pro',
    tier: 'pro',
    nameMessageId: 'app.sml.pricing.plan.pro.name',
    nameDefault: 'Pro',
    descriptionMessageId: 'app.sml.pricing.plan.pro.description',
    descriptionDefault: 'Full-song videos, AI clips and your own domain.',
    mostPopular: true,
    prices: pricesOf('pro', { monthly: [], yearly: [] }),
  },
  {
    id: 'plug',
    tier: 'plug',
    nameMessageId: 'app.sml.pricing.plan.plug.name',
    nameDefault: 'Plug',
    descriptionMessageId: 'app.sml.pricing.plan.plug.description',
    descriptionDefault: 'Pitch every release to the playlists and blogs that fit.',
    mostPopular: false,
    prices: pricesOf('plug', { monthly: [], yearly: [] }),
  },
  {
    id: 'manager',
    tier: 'manager',
    nameMessageId: 'app.sml.pricing.plan.manager.name',
    nameDefault: 'Max',
    descriptionMessageId: 'app.sml.pricing.plan.manager.description',
    descriptionDefault: 'More posts, more video credits and API access.',
    mostPopular: false,
    prices: pricesOf('manager', {
      monthly: ['price_1U81ObH5lKCcz1F2gK1Z1ZvJ', 'price_1U81NDHDHBS69TCVpek6Qh1U'],
      yearly: ['price_1U81OdH5lKCcz1F23Xvxgyy2', 'price_1U81NFHDHBS69TCVcLCxi9FE'],
    }),
  },
  {
    id: 'label',
    tier: 'label',
    nameMessageId: 'app.sml.pricing.plan.label.name',
    nameDefault: 'Ultra',
    descriptionMessageId: 'app.sml.pricing.plan.label.description',
    descriptionDefault: 'The highest limits, Autopilot pitching and API access.',
    mostPopular: false,
    prices: pricesOf('label', {
      monthly: ['price_1U81OgH5lKCcz1F2jbLL89fD', 'price_1U81NHHDHBS69TCVVCFMeWH6'],
      yearly: ['price_1U81OhH5lKCcz1F2wVbKEv3O', 'price_1U81NJHDHBS69TCVn3Fp2sd9'],
    }),
  },
];

/**
 * The plans for every offer that is not about Outreach, cheapest first: `MUSIC24_PLANS` without
 * Plug. Plug has the Pro limits, so it is never the answer to "which plan gives more of this".
 */
export const MUSIC24_GENERAL_PLANS: Music24Plan[] = MUSIC24_PLANS.filter(
  (plan) => plan.id !== MUSIC24_OUTREACH_PLAN_ID
);

/** What a checkout sends: the server finds the current Stripe price of the tier and interval. */
export function music24CheckoutPlanInput(
  plan: Music24Plan,
  interval: BillingInterval
): { tier: Music24PlanId; interval: BillingInterval } {
  return { tier: plan.id, interval };
}

export function yearlyToMonthly(yearlyAmountCents: number): number {
  return Math.round(yearlyAmountCents / 12);
}
